/* Invite-only onboarding page.
   - Reads the ?token= from the URL
   - Calls /api/invitations/peek to verify validity
   - On submit, calls /api/auth/redeem-invitation
*/
import { useEffect, useState } from "react";
import { useSearchParams, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import PilotDemoBanner from "@/components/PilotDemoBanner";
import { useUserAuth } from "@/context/UserAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InviteAccept() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user } = useUserAuth();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [tos, setTos] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [emailOpt, setEmailOpt] = useState(false);
  const [smsOpt, setSmsOpt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setErr("Missing invitation token.");
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/invitations/peek`, { params: { token } })
      .then((r) => setInvite(r.data.invitation))
      .catch((e) => setErr(e?.response?.data?.detail || "This invitation isn't available."))
      .finally(() => setLoading(false));
  }, [token]);

  if (user) return <Navigate to="/settings" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/redeem-invitation`, {
        token,
        password,
        display_name: displayName,
        accept_terms: tos,
        accept_privacy_notice: privacy,
        email_opt_in: emailOpt,
        sms_opt_in: smsOpt,
      });
      localStorage.setItem("who.user.token", r.data.token);
      setDone(true);
      setTimeout(() => (window.location.href = "/settings"), 800);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Unable to accept this invitation.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] bg-cream" data-testid="invite-accept-page">
      <Seo title="Accept your WHO invitation" path="/invite/accept" description="Redeem a WHO pilot invitation to create your account." />
      <PilotDemoBanner text="WHO is invite-only during the pilot. Thank you for being an early tester." />
      <div className="max-w-xl mx-auto px-6 lg:px-10 py-14">
        <p className="eyebrow text-gold-deep">Invitation</p>
        <h1 className="mt-3 font-display font-black text-navy text-3xl lg:text-4xl flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-gold-deep" /> Welcome to WHO.
        </h1>

        {loading && <p className="mt-6 text-slateSoft">Checking your invitation…</p>}
        {!loading && err && !invite && (
          <div className="mt-8 p-6 bg-white border border-borderSubtle rounded-xl" data-testid="invite-error">
            <p className="text-navy font-semibold">{err}</p>
            <p className="mt-3 text-sm text-slateSoft">Invitations are single-use and expire after 72 hours by default. If you believe this is a mistake, contact the WHO team.</p>
            <Link to="/" className="mt-5 inline-block text-sm text-navy underline underline-offset-2">Back to home</Link>
          </div>
        )}

        {invite && !done && (
          <form onSubmit={submit} className="mt-8 bg-white border border-borderSubtle rounded-xl p-6 lg:p-8 space-y-5" data-testid="invite-form">
            <div className="rounded-md bg-cream-panel border border-borderSubtle px-4 py-3">
              <p className="text-xs uppercase tracking-widest text-navy/60 font-semibold">You&rsquo;re joining as</p>
              <p className="mt-1 font-display font-bold text-navy text-lg capitalize" data-testid="invite-role">{invite.role.replace(/_/g, " ")}</p>
              <p className="text-xs text-slateSoft mt-1">Email: {invite.email}{invite.provider_id ? ` · Provider: ${invite.provider_id}` : ""}</p>
            </div>

            {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="invite-form-error">{err}</p>}

            <div>
              <label className="block text-xs uppercase tracking-widest text-navy/60 font-semibold mb-1.5" htmlFor="iv-name">Name</label>
              <input id="iv-name" data-testid="invite-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="w-full px-3 py-2.5 bg-white border border-borderSubtle rounded-md focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-navy/60 font-semibold mb-1.5" htmlFor="iv-pass">Password <span className="normal-case tracking-normal text-slateSoft">(minimum 12 characters)</span></label>
              <input id="iv-pass" data-testid="invite-password" type="password" required minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2.5 bg-white border border-borderSubtle rounded-md focus:outline-none focus:border-gold" />
            </div>

            <div className="pt-2 border-t border-borderSubtle space-y-2 text-sm text-navy">
              <label className="flex items-start gap-3"><input type="checkbox" className="mt-1 h-4 w-4 accent-gold-deep" checked={tos} onChange={(e) => setTos(e.target.checked)} data-testid="invite-tos" /><span>I accept WHO&rsquo;s <a href="/sources#consent" className="underline underline-offset-2 font-semibold">Terms of Service</a> <span className="text-[10px] uppercase tracking-widest text-gold-deep font-bold ml-1">Required</span></span></label>
              <label className="flex items-start gap-3"><input type="checkbox" className="mt-1 h-4 w-4 accent-gold-deep" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="invite-privacy" /><span>I acknowledge WHO&rsquo;s <a href="/sources#consent" className="underline underline-offset-2 font-semibold">Privacy Notice</a> <span className="text-[10px] uppercase tracking-widest text-gold-deep font-bold ml-1">Required</span></span></label>
              <label className="flex items-start gap-3"><input type="checkbox" className="mt-1 h-4 w-4 accent-gold-deep" checked={emailOpt} onChange={(e) => setEmailOpt(e.target.checked)} data-testid="invite-email-opt" /><span>I agree to receive <strong>service-related emails</strong> about my warm handoffs. <span className="text-[10px] uppercase tracking-widest text-navy/50 font-bold ml-1">Optional</span></span></label>
              <label className="flex items-start gap-3"><input type="checkbox" className="mt-1 h-4 w-4 accent-gold-deep" checked={smsOpt} onChange={(e) => setSmsOpt(e.target.checked)} data-testid="invite-sms-opt" /><span>I agree to receive <strong>service-related SMS</strong>. <span className="text-[10px] uppercase tracking-widest text-navy/50 font-bold ml-1">Optional</span></span></label>
            </div>

            <button
              type="submit"
              disabled={busy || !tos || !privacy || password.length < 12}
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-5 py-3 rounded-md disabled:opacity-50"
              data-testid="invite-submit"
            >
              {busy ? "Creating account…" : "Accept and create account"}
            </button>
          </form>
        )}

        {done && (
          <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-xl" data-testid="invite-success">
            <p className="text-green-800 font-semibold">Welcome aboard. Taking you to your settings…</p>
          </div>
        )}
      </div>
    </div>
  );
}
